/** A dropdown you can type into.
 *
 * Replaces a plain <select> wherever the list is long enough that scrolling for the right
 * entry is the slow part — thirty-odd projects, the whole people directory. Typing narrows
 * the list; arrow keys and Enter pick; Escape or clicking away puts back what was there.
 *
 * Options may carry a `group`. Groups render in the order they first appear, which is how
 * a caller puts "yours" above "everything else" without this component knowing what
 * either means.
 *
 * Deliberately no free text: the value is always one of the options, or the empty option.
 * A typed name that matches nothing is exactly the unlinked-string problem the pickers
 * exist to prevent.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { inputClass } from '@/components/ui'

export interface SearchOption {
  value: string
  label: string
  /** Shown after the label, muted. Matched by the search too. */
  hint?: string
  group?: string
}

export function SearchSelect({
  value,
  onChange,
  options,
  emptyLabel,
  placeholder = 'Type to search…',
  disabled = false,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  options: SearchOption[]
  /** The label of the "nothing chosen" option, e.g. "All projects". Omit to offer none. */
  emptyLabel?: string
  placeholder?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)

  const selected = options.find((option) => option.value === value)
  // A value with no matching option — an archived project still linked to a work item —
  // is said out loud rather than shown blank, which would read as "nothing chosen" while
  // saving would keep it.
  const shownLabel =
    selected?.label ?? (value === '' ? (emptyLabel ?? '') : 'Not in this list (kept as is)')

  const choices = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = needle
      ? options.filter((option) =>
          `${option.label} ${option.hint ?? ''}`.toLowerCase().includes(needle),
        )
      : options
    // The empty option only while not searching: typing is looking for something.
    return emptyLabel !== undefined && !needle
      ? [{ value: '', label: emptyLabel } as SearchOption, ...matches]
      : matches
  }, [options, query, emptyLabel])

  // Clicking anywhere else closes without changing anything.
  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return
    document.getElementById(`${listId}-${cursor}`)?.scrollIntoView({ block: 'nearest' })
  }, [cursor, open, listId])

  function openList() {
    if (disabled) return
    setQuery('')
    setCursor(Math.max(0, choices.findIndex((option) => option.value === value)))
    setOpen(true)
  }

  function close() {
    setOpen(false)
    setQuery('')
  }

  function pick(option: SearchOption | undefined) {
    if (!option) return
    onChange(option.value)
    close()
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      event.preventDefault()
      openList()
      return
    }
    if (!open) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((current) => Math.min(choices.length - 1, current + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((current) => Math.max(0, current - 1))
    } else if (event.key === 'Enter') {
      // Inside a dialog's form, Enter would otherwise submit it mid-choice.
      event.preventDefault()
      pick(choices[cursor])
    } else if (event.key === 'Escape') {
      // Stop here, so the Escape that closes this list does not also close the dialog.
      event.stopPropagation()
      event.nativeEvent.stopPropagation()
      close()
    } else if (event.key === 'Tab') {
      close()
    }
  }

  let lastGroup: string | undefined

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-activedescendant={open && choices[cursor] ? `${listId}-${cursor}` : undefined}
        className={`${inputClass} pr-8 ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        value={open ? query : shownLabel}
        placeholder={open ? placeholder : (emptyLabel ?? placeholder)}
        disabled={disabled}
        onFocus={openList}
        // mousedown rather than click. These usually sit inside a <label>, and a label
        // forwards every click inside it — including the one that just picked an option —
        // to the input as a synthetic click, which would reopen the list at once.
        onMouseDown={() => (open ? null : openList())}
        onChange={(event) => {
          setQuery(event.target.value)
          setCursor(0)
          if (!open) setOpen(true)
        }}
        onKeyDown={onKeyDown}
      />
      <ChevronDown
        size={14}
        aria-hidden
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-muted"
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="card absolute left-0 right-0 top-full z-50 mt-1 max-h-72 min-w-[220px] overflow-auto py-1 text-sm shadow-lg"
        >
          {choices.length === 0 ? (
            <li className="px-3 py-2 text-fg-muted">Nothing matches “{query.trim()}”.</li>
          ) : (
            choices.map((option, index) => {
              const heading =
                option.group && option.group !== lastGroup ? option.group : undefined
              lastGroup = option.group ?? lastGroup
              return (
                <li key={`${option.group ?? ''}-${option.value}`} role="presentation">
                  {heading ? (
                    <div className="micro-label px-3 pb-1 pt-2 text-fg-muted">{heading}</div>
                  ) : null}
                  <div
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={option.value === value}
                    className={`cursor-pointer px-3 py-1.5 ${
                      index === cursor ? 'bg-canvas' : ''
                    } ${option.value === value ? 'font-semibold text-fg' : 'text-fg'}`}
                    // mousedown, not click: the input's blur would otherwise land first.
                    onMouseDown={(event) => {
                      event.preventDefault()
                      pick(option)
                    }}
                    onMouseEnter={() => setCursor(index)}
                  >
                    {option.label}
                    {option.hint ? (
                      <span className="ml-2 text-xs text-fg-muted">{option.hint}</span>
                    ) : null}
                  </div>
                </li>
              )
            })
          )}
        </ul>
      ) : null}
    </div>
  )
}
